window.theme = window.theme || {};

// Init section function when it's visible, then disable observer
theme.initWhenVisible = theme.initWhenVisible || function(options) {
  const threshold = options.threshold ? options.threshold : 0;

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (typeof options.callback === 'function') {
          options.callback();
          observer.unobserve(entry.target);
        }
      }
    });
  }, {rootMargin: `0px 0px ${threshold}px 0px`});
 
  observer.observe(options.element);
};

class ScrollingReviews extends HTMLElement {
  constructor() {
    super();

    this.config = {
      moveTime: parseFloat(this.dataset.speed),
      space: 100,
      mobileBreakpoint: 750
    };

    this.reviews = this.querySelector('.reviews');
    this.cards = this.querySelectorAll('.phi--review-card');
    this.dots = this.querySelectorAll('.phi-m--reviews-dot');
    
    // Check device type
    this.checkDevice();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });

    // Handle resize with debounce
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.handleResize(), 150);
    });
  }

  checkDevice() {
    this.isMobile = window.innerWidth < this.config.mobileBreakpoint;
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.checkDevice();
    
    // If device type changed, reinitialize
    if (wasMobile !== this.isMobile) {
      location.reload();
    }
  }

  init() {
    if (this.isMobile) {
      this.phiMobileInit();
    } else {
      this.phiDesktopInit();
    }
  }

  // ==========================================
  // PHI-M: MOBILE - Snap Scroll with Dots
  // ==========================================
  
  phiMobileInit() {
    // Add mobile class
    this.classList.add('phi-m--active');
    
    // Add scroll listener for updating dots
    this.reviews.addEventListener('scroll', this.phiMobileHandleScroll.bind(this));
    
    // Add click listeners for dots
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.phiMobileScrollToCard(index));
    });
  }

  phiMobileHandleScroll() {
    if (!this.isMobile) return;
    
    const scrollLeft = this.reviews.scrollLeft;
    const containerWidth = this.reviews.offsetWidth;
    
    let activeIndex = 0;
    let minDistance = Infinity;
    
    this.cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + (card.offsetWidth / 2) - this.reviews.offsetLeft;
      const containerCenter = scrollLeft + (containerWidth / 2);
      const distance = Math.abs(cardCenter - containerCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });
    
    this.phiMobileUpdateActiveDot(activeIndex);
  }

  phiMobileUpdateActiveDot(activeIndex) {
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
    });
  }

  phiMobileScrollToCard(index) {
    const card = this.cards[index];
    if (!card) return;
    
    const containerWidth = this.reviews.offsetWidth;
    const cardWidth = card.offsetWidth;
    const scrollPosition = card.offsetLeft - (containerWidth / 2) + (cardWidth / 2);
    
    this.reviews.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  }

  // ==========================================
  // PHI-D: DESKTOP - Marquee Animation
  // ==========================================
  
  phiDesktopInit() {
    // Add desktop class
    this.classList.add('phi-d--active');
    
    // Check if marquee hasn't been initialized yet (no cloned reviews)
    const reviewsContainers = this.querySelectorAll('.reviews');
    if (reviewsContainers.length === 1) {
      this.reviews.classList.add('reviews--animated');

      // Clone reviews for infinite scroll effect
      for (let index = 0; index < 10; index++) {
        this.clone = this.reviews.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        // Insert before dots container
        this.reviews.parentNode.insertBefore(this.clone, this.querySelector('.phi-m--reviews-dots'));
      }

      // Calculate animation duration based on content width
      const animationTimeFrame = (this.reviews.clientWidth / this.config.space) * this.config.moveTime;
      this.style.setProperty('--duration', `${animationTimeFrame}s`);

      // Pause when out of view for performance
      const observer = new IntersectionObserver((entries, _observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.phiDesktopPlay();
          } else {
            this.phiDesktopPause();
          }
        });
      }, {rootMargin: '0px 0px 50px 0px'});

      observer.observe(this);
    }
  }

  phiDesktopPlay() {
    this.classList.remove('scrolling-reviews--paused');
  }

  phiDesktopPause() {
    this.classList.add('scrolling-reviews--paused');
  }
}

customElements.define('scrolling-reviews', ScrollingReviews);
